import { inject } from "fw";
import { Store, handle } from "fw-state";
import { LogoutAction, StartAction } from "./actions";
import { AtsRepository } from "network/ats-repository";
import { AtsOrganization } from "models/ats-organization";
import { AtsUser } from "models/ats-user";
import { LocalStorageCache } from "caching";

const MAX_SKIP = 2000;
const DEFAULT_PAGE_SIZE = 20;
const SEARCH_CACHE_KEY = "global-search-tab-selection";

export enum SearchType {
    Users = "Users",
    Organizations = "Organizations"
}

interface SearchShape {
    loaded: boolean;
    type: SearchType;
    collection: Array<AtsUser | AtsOrganization>;
    totalCount: number;
    filter: string;
    searchTerms: { [SearchType.Users]: string; [SearchType.Organizations]: string };
    sort: string;
    selectAll: boolean;

    pageSize: number;
    currentPage: number;
    lastPage: number;
    pageStartNumber: number;
    pageEndNumber: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    canAdvancePage: boolean;
}

export class EnsureSearchAction {
    constructor(
        public refresh: boolean = false,
        public type: SearchType
    ) {}
}
export class FetchMoreInfo {
    constructor(
        public id: string,
        public refresh: boolean = false
    ) {}
}
export class UpdateSearchFiltersAction {
    constructor(
        public type: SearchType = null,
        public filter: string = null,
        public searchTerms: { [SearchType.Users]: string; [SearchType.Organizations]: string } = null,
        public sort: string = null
    ) {}
}
export class SetSearchPageAction {
    constructor(public page: number = 1) {}
}
export class PageForwardSearchAction {}
export class PageBackwardSearchAction {}
export class UpdatePageSearchAction {
    constructor(public page: number) {}
}
export class SetSearchSortAction {
    constructor(public sort: string) {}
}

export class SetSearchTypeAction {
    constructor(public type: SearchType) {}
}

@inject
export class SearchStore extends Store<SearchShape> {
    constructor(
        private atsRepository: AtsRepository,
        private cache: LocalStorageCache
    ) {
        super();
    }

    defaultState() {
        return {
            loaded: false,
            type: SearchType.Users,
            collection: [],
            totalCount: 0,
            filter: null,
            searchTerms: {
                [SearchType.Users]: "",
                [SearchType.Organizations]: ""
            },
            sort: null,
            selectAll: false,

            pageSize: DEFAULT_PAGE_SIZE,
            currentPage: 1,
            lastPage: 1,
            pageStartNumber: 1,
            pageEndNumber: DEFAULT_PAGE_SIZE,
            hasPreviousPage: false,
            hasNextPage: false,
            canAdvancePage: false
        };
    }

    private restoreSearchType() {
        if (!this.cache) {
            return;
        }

        const cachedType = this.cache.get<SearchType>(SEARCH_CACHE_KEY);
        if (cachedType) {
            this.setState((state) => ({
                ...state,
                type: cachedType
            }));
        }
    }

    public resetState() {
        this.setState(() => this.defaultState());
        this.restoreSearchType();
    }

    // @handle(StartAction)
    // private handleStartAction(action: StartAction) {
    //   this.setState(() => this.defaultState());
    // }

    // @handle(LogoutAction)
    // private handleLogoutAction(action: LogoutAction) {
    //   this.setState(() => this.defaultState());
    // }

    @handle(StartAction)
    @handle(LogoutAction)
    private handleDefaultAction() {
        this.resetState();
    }

    @handle(EnsureSearchAction)
    private async handleEnsureSearchAction(action: EnsureSearchAction) {
        if (this.state.loaded && !action.refresh && !action.type) return;

        this.search();
    }

    @handle(FetchMoreInfo)
    private async handleFetchMoreInfo(action: FetchMoreInfo) {
        if (!action.id) return;

        const item = this.state.collection.find((i) => i.Id == action.id);
        if (item && !action.refresh) return;

        const newItem = await (this.state.type == "Users" ? this.atsRepository.userById(action.id) : this.atsRepository.organizationById(action.id));
        const collection = this.state.collection.slice();

        if (item) collection[collection.indexOf(item)] = newItem;
        else collection.push(newItem);

        this.setState((state) => ({ ...state, collection: collection }));
    }

    @handle(SetSearchTypeAction)
    private handleSetSearchTypeAction(action: SetSearchTypeAction) {
        this.setState((state) => ({
            ...state,
            type: action.type
        }));

        this.cache.set<SearchType>(SEARCH_CACHE_KEY, action.type);
    }

    @handle(UpdateSearchFiltersAction)
    private handleUpdateSearchFilters(action: UpdateSearchFiltersAction) {
        this.setState(() => ({
            ...this.defaultState(), // new search should always reset paging
            type: action.type ?? SearchType.Users,
            filter: action.filter,
            searchTerms: { ...action.searchTerms },
            sort: action.sort,
            loaded: false
        }));

        this.search();
    }

    @handle(SetSearchPageAction)
    private handleSetSearchPageAction(action: SetSearchPageAction) {
        this.setState((state) => ({
            ...state,
            currentPage: action.page
        }));

        this.search();
    }

    @handle(PageForwardSearchAction)
    private handlePageForward(action: PageForwardSearchAction) {
        this.setState((state) => ({
            ...state,
            currentPage: state.canAdvancePage ? state.currentPage++ : state.currentPage
        }));

        this.search();
    }

    @handle(PageBackwardSearchAction)
    private handlePageBackward(action: PageBackwardSearchAction) {
        this.setState((state) => ({
            ...state,
            currentPage: state.currentPage > 0 ? state.currentPage-- : state.currentPage
        }));

        this.search();
    }

    @handle(UpdatePageSearchAction)
    private handleUpdatePage(action: UpdatePageSearchAction) {
        this.setState((state) => ({
            ...state,
            currentPage: action.page > 0 && action.page <= state.lastPage ? action.page : state.currentPage
        }));

        this.search();
    }

    @handle(SetSearchSortAction)
    private async handleToggleSearchSortAction(ts: SetSearchSortAction) {
        const sort = ts.sort == this.state.sort ? `-(${ts.sort})` : ts.sort;
        this.setState((state) => ({
            ...state,
            sort
        }));

        this.search();
    }

    private async search() {
        const { filter, searchTerms, sort, pageSize, type } = this.state;
        let { currentPage } = this.state;

        const res = await (type == SearchType.Users
            ? this.atsRepository.users(filter, searchTerms[type], sort, currentPage, pageSize)
            : this.atsRepository.organizations(filter, searchTerms[type], sort, currentPage, pageSize));
        const totalCount = res.total;
        const lastPage = Math.ceil(totalCount / pageSize);
        currentPage = Math.min(currentPage, lastPage);

        const pageStartNumber = (currentPage - 1) * pageSize + 1;
        const pageEndNumber = pageStartNumber + (pageSize - 1);
        const hasNextPage = pageEndNumber < totalCount;
        const hasPreviousPage = pageStartNumber > 1;
        const canAdvancePage = pageEndNumber + 1 < MAX_SKIP;

        this.setState((state) => ({
            ...state,
            loaded: true,
            collection: res.list,
            totalCount,
            currentPage,
            pageStartNumber: Math.max(pageStartNumber, 0),
            pageEndNumber: Math.min(pageEndNumber, totalCount),
            hasNextPage,
            hasPreviousPage,
            canAdvancePage,
            lastPage
        }));
    }
}
